import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, Loader2, X, Users, Key, Database } from 'lucide-react';

export default function AdminCompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', active: true, custom_api_keys: false, custom_qdrant: false });

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const data = await api.get('/api/companies');
      setCompanies(data);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', code: '', active: true, custom_api_keys: false, custom_qdrant: false });
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, code: c.code, active: c.active, custom_api_keys: c.custom_api_keys || false, custom_qdrant: c.custom_qdrant || false });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/api/companies/${editing.id}`, form);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/api/companies', form);
        toast.success('Empresa creada');
      }
      setShowModal(false);
      loadCompanies();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"? Los usuarios de esta empresa quedarán sin empresa.`)) return;
    try {
      await api.del(`/api/companies/${c.id}`);
      toast.success('Empresa eliminada');
      loadCompanies();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 size={28} className="animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-4xl" data-testid="admin-companies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Gestion de Empresas</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{companies.length} empresas</p>
        </div>
        <button onClick={openCreate} data-testid="create-company-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
          <Plus size={16} /> Nueva Empresa
        </button>
      </div>

      <div className="grid gap-3">
        {companies.map(c => (
          <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid={`company-${c.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--accent-primary)', opacity: c.active ? 1 : 0.4 }}>
                <Building2 size={18} className="text-white" />
              </div>
              <div>
                <h3 className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{c.name}</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-muted)' }}>{c.code}</span>
                  <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    <Users size={10} /> {c.user_count || 0} usuarios
                  </span>
                  {c.custom_api_keys && <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400"><Key size={9} /> APIs</span>}
                  {c.custom_qdrant && <span className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400"><Database size={9} /> Qdrant</span>}
                  <span className={`w-1.5 h-1.5 rounded-full ${c.active ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`} className="p-2 rounded-md transition-colors hover:bg-white/10" style={{ color: 'var(--text-muted)' }}>
                <Edit size={14} />
              </button>
              <button onClick={() => handleDelete(c)} data-testid={`delete-company-${c.id}`} className="p-2 rounded-md transition-colors hover:bg-red-500/10" style={{ color: 'var(--text-muted)' }}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {companies.length === 0 && (
          <div className="text-center py-10 rounded-xl border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            <Building2 size={28} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No hay empresas creadas</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border p-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }} data-testid="company-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{editing ? 'Editar Empresa' : 'Nueva Empresa'}</h3>
              <button onClick={() => setShowModal(false)} style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Nombre</label>
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="company-name"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Codigo</label>
                <input type="text" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} data-testid="company-code"
                  className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} data-testid="company-active" />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Activa</span>
              </div>
              <div className="pt-3 mt-1 border-t" style={{ borderColor: 'var(--border-color)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Permisos de personalizacion</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-custom-api-keys">
                    <div onClick={() => setForm(p => ({ ...p, custom_api_keys: !p.custom_api_keys }))} className="relative">
                      {form.custom_api_keys ? (
                        <div className="w-9 h-5 rounded-full bg-blue-500 relative transition-colors"><div className="w-3.5 h-3.5 bg-white rounded-full absolute right-0.5 top-[3px] transition-all" /></div>
                      ) : (
                        <div className="w-9 h-5 rounded-full relative transition-colors" style={{ backgroundColor: 'var(--border-color)' }}><div className="w-3.5 h-3.5 bg-white rounded-full absolute left-0.5 top-[3px] transition-all" /></div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Claves API propias</span>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Permite configurar OpenAI y Deepgram propios</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-custom-qdrant">
                    <div onClick={() => setForm(p => ({ ...p, custom_qdrant: !p.custom_qdrant }))} className="relative">
                      {form.custom_qdrant ? (
                        <div className="w-9 h-5 rounded-full bg-purple-500 relative transition-colors"><div className="w-3.5 h-3.5 bg-white rounded-full absolute right-0.5 top-[3px] transition-all" /></div>
                      ) : (
                        <div className="w-9 h-5 rounded-full relative transition-colors" style={{ backgroundColor: 'var(--border-color)' }}><div className="w-3.5 h-3.5 bg-white rounded-full absolute left-0.5 top-[3px] transition-all" /></div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Qdrant propio</span>
                      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Permite configurar instancia Qdrant propia</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>Cancelar</button>
              <button onClick={handleSave} data-testid="save-company-btn" className="px-5 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                {editing ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
